import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const DEFAULT_SLIDES = [
  {
    id: 'slide-1',
    title: "Colección Exclusiva",
    discount: "Hasta 30% OFF",
    icon: "fa-solid fa-star",
    backgroundColor: "#388E3C",
    backgroundImage: "/images/slider/sticker1.jpg"
  },
  {
    id: 'slide-2',
    title: "Estilo Urbano Premium",
    discount: "Envíos a todo el país",
    icon: "fa-solid fa-truck-fast",
    backgroundColor: "#43A047",
    backgroundImage: "/images/slider/sticker2.jpg"
  },
  {
    id: 'slide-3',
    title: "Decora tu Hogar",
    discount: "Cuadros y Almohadones",
    icon: "fa-solid fa-couch",
    backgroundColor: "#2E7D32",
    backgroundImage: "/images/slider/sticker3.jpg"
  },
  {
    id: 'slide-4',
    title: "Únete a la Revolución IA",
    discount: "Diseños Únicos",
    icon: "fa-brands fa-reddit-alien",
    backgroundColor: "#1B5E20",
    backgroundImage: "/images/slider/sticker4.jpg"
  }
];

const InicioSlider = () => {
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [slideIndex, setSlideIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const fetchSliderSettings = async () => {
      try {
        const response = await fetch('/api/settings');
        if (!response.ok) return;

        const data = await response.json();
        const configuredSlides = data?.homeSlider?.slides || [];
        const activeSlides = configuredSlides.filter(slide => slide.isActive !== false && slide.backgroundImage);

        if (activeSlides.length > 0) {
          setSlides(activeSlides);
          setSlideIndex(0);
        }
      } catch (error) {
        console.error('Error fetching home slider settings:', error);
      }
    };

    fetchSliderSettings();
  }, []);

  const changeSlide = (newIndex) => {
    if (isAnimating) return;
    setIsAnimating(true);
    let targetIndex = newIndex;
    if (targetIndex < 0) targetIndex = slides.length - 1;
    if (targetIndex >= slides.length) targetIndex = 0;

    setSlideIndex(targetIndex);

    // Reactivate clicks after transition duration
    setTimeout(() => {
      setIsAnimating(false);
    }, 800);
  };

  // Auto slide effect
  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const slideTimer = setInterval(() => {
      changeSlide(slideIndex + 1);
    }, 6000);
    return () => clearInterval(slideTimer);
  }, [slideIndex, isAnimating, slides.length]);

  return (
    <div
      className="modern-hero-slider"
      style={{
        position: 'relative',
        width: '100%',
        height: 'calc(100vh - 80px)', // Full bleed, minus header height approx
        minHeight: 'var(--home-hero-min-height, 400px)',
        maxHeight: 'var(--home-hero-max-height, clamp(420px, 60vh, 580px))',
        overflow: 'hidden',
        backgroundColor: '#000'
      }}
    >
      {/* Slider Container */}
      <div
        style={{
          display: 'flex',
          width: `${slides.length * 100}%`,
          height: '100%',
          transform: `translateX(-${slideIndex * (100 / slides.length)}%)`,
          transition: 'transform 0.8s cubic-bezier(0.645, 0.045, 0.355, 1)'
        }}
      >
        {slides.map((offer, index) => (
          <div
            key={offer.id}
            style={{
              width: `${100 / slides.length}%`,
              height: '100%',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
          >
            {/* Background Image with Ken Burns animation if it's the active slide */}
            <div
              className={index === slideIndex ? "slide-bg-anim" : ""}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${getDisplayImageUrl(offer.backgroundImage)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                willChange: 'transform'
              }}
            />

            {/* Dark Premium Overlays */}
            <div
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.8) 100%)',
                zIndex: 1
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 0, left: 0, right: 0, height: '40%',
                background: 'linear-gradient(to top, #000 0%, transparent 100%)',
                zIndex: 1
              }}
            />

            {/* Slide Content */}
            <div
              style={{
                position: 'relative',
                zIndex: 2,
                textAlign: 'center',
                padding: '0 20px',
                width: 'var(--home-hero-content-width, calc(100% - 140px))',
                maxWidth: '900px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <div
                className={index === slideIndex ? "slide-content-anim" : ""}
                style={{
                  width: 'var(--home-hero-icon-size, 80px)',
                  height: 'var(--home-hero-icon-size, 80px)',
                  borderRadius: '50%',
                  background: 'rgba(31, 142, 56, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(31, 142, 56, 0.4)',
                  boxShadow: '0 0 20px rgba(31, 142, 56, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem'
                }}
              >
                <i className={`${offer.icon} fa-2x text-brand-green`} style={{ filter: 'drop-shadow(0 0 8px rgba(31,142,56,0.8))' }}></i>
              </div>

              <h1
                className={index === slideIndex ? "slide-content-anim slide-content-anim-delay-1" : ""}
                style={{
                  fontSize: 'var(--home-hero-title-size, clamp(2.5rem, 8vw, 4.2rem))',
                  fontWeight: '800',
                  color: '#ffffff',
                  lineHeight: '1.1',
                  marginBottom: '1rem',
                  textShadow: '0 4px 15px rgba(0,0,0,1)',
                  letterSpacing: '-0.5px'
                }}
              >
                {offer.title}
              </h1>

              <div
                className={index === slideIndex ? "slide-content-anim slide-content-anim-delay-2" : ""}
                style={{
                  background: 'linear-gradient(135deg, rgba(31, 142, 56, 0.9) 0%, rgba(37, 160, 0, 0.8) 100%)',
                  backdropFilter: 'blur(10px)',
                  padding: '6px 20px',
                  borderRadius: '50px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 8px 25px rgba(31, 142, 56, 0.4)'
                }}>
                <p style={{
                  fontSize: 'clamp(1rem, 2vw, 1.4rem)',
                  fontWeight: '700',
                  color: '#ffffff',
                  margin: 0,
                  letterSpacing: '0.5px'
                }}>
                  {offer.discount}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      <div
        className="slider-controls"
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          transform: 'translateY(-50%)',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 4vw',
          zIndex: 10,
          pointerEvents: 'none'
        }}
      >
        {slides.length > 1 && (
          <>
        <button
          onClick={() => changeSlide(slideIndex - 1)}
          className="btn-outline-brand-green"
          style={{
            pointerEvents: 'auto',
            width: 'clamp(40px, 12vw, 60px)',
            height: 'clamp(40px, 12vw, 60px)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'clamp(1rem, 4vw, 1.5rem)',
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(5px)',
            cursor: 'pointer'
          }}
        >
          <i className="fa-solid fa-arrow-left"></i>
        </button>

        <button
          onClick={() => changeSlide(slideIndex + 1)}
          className="btn-outline-brand-green"
          style={{
            pointerEvents: 'auto',
            width: 'clamp(40px, 12vw, 60px)',
            height: 'clamp(40px, 12vw, 60px)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'clamp(1rem, 4vw, 1.5rem)',
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(5px)',
            cursor: 'pointer'
          }}
        >
          <i className="fa-solid fa-arrow-right"></i>
        </button>
          </>
        )}
      </div>

      {/* Modern Progress Bullets */}
      <div
        style={{
          position: 'absolute',
          bottom: '40px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          zIndex: 10
        }}
      >
        {slides.map((_, idx) => (
          <div
            key={idx}
            onClick={() => changeSlide(idx)}
            style={{
              width: idx === slideIndex ? '40px' : '15px',
              height: '4px',
              borderRadius: '4px',
              backgroundColor: idx === slideIndex ? '#1f8e38' : 'rgba(255,255,255,0.3)',
              cursor: 'pointer',
              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              boxShadow: idx === slideIndex ? '0 0 10px rgba(31,142,56,0.8)' : 'none'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default InicioSlider;
